import React from "react";
import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import InfoIcon from "../../svg/InfoIcon";
import LocalizedNumber from "./LocalizedNumber";

export type UnsavedBiomarkerItem = {
  name: string;
  value: string | number;
  unit: string;
  // When set, the row shows this reason (wrapping, under the name) instead of
  // the value/unit — used for failed biomarkers.
  reason?: string;
};

interface UnsavedBiomarkersNoticeProps {
  title: string;
  message: string;
  items: UnsavedBiomarkerItem[];
  // Merged over the card's own style (e.g. to drop the top margin when the
  // notice is the first thing in a list).
  style?: React.CSSProperties;
}

// Count-aware copy for the notice, e.g. "We detected 1 biomarker we couldn't
// match to our database, so it won't be saved." / "...3 biomarkers...so they
// won't be saved." `outcome` is the last verb ("saved", "imported").
export const unsavedNoticeMessage = (
  count: number,
  reason: string,
  outcome = "saved"
) =>
  `We detected ${count} biomarker${count === 1 ? "" : "s"} ${reason}, so ${
    count === 1 ? "it won’t" : "they won’t"
  } be ${outcome}.`;

const UnsavedBiomarkersNotice: React.FC<UnsavedBiomarkersNoticeProps> = ({
  title,
  message,
  items,
  style,
}) => {
  const theme: Theme = useTheme();

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div style={{ ...styles.container(theme), ...style }}>
      <div style={styles.header}>
        <InfoIcon size={12} color={theme.colors_ds.NEUTRAL_TEXT_DEFAULT} />
        <div style={styles.title(theme)}>{title}</div>
      </div>
      <div style={styles.message(theme)}>{message}</div>
      <div style={styles.itemList}>
        {items.map((item, index) => (
          <div key={index} style={styles.itemRow(theme)}>
            <div style={styles.itemName(theme)}>{item.name}</div>
            {item.reason ? (
              <div style={styles.itemReason(theme)}>{item.reason}</div>
            ) : (
              <div style={styles.itemValue(theme)}>
                <LocalizedNumber value={item.value} />
                {item.unit ? ` ${item.unit}` : ""}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default UnsavedBiomarkersNotice;

const styles = {
  container: (theme: Theme) => ({
    backgroundColor: theme.colorTokens.W[10],
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "12px 16px",
    marginTop: "16px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  }),
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  message: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
  }),
  itemList: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
    marginTop: "4px",
  },
  itemRow: (theme: Theme) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    padding: "6px 0",
    borderTop: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  itemName: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    flex: 1,
  }),
  itemValue: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    whiteSpace: "nowrap" as "nowrap",
  }),
  // Failure reason shown on the same row as the name, aligned right.
  itemReason: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "17px",
    textAlign: "right" as "right",
    flexShrink: 1,
  }),
};
